import React from 'react';
import { Route, Zap, Circle, ArrowRight } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { calculateSingleItemTotals } from '../../services/calculations';

export const WireScheduleList: React.FC = () => {
  const { lineItems, activeRowIndex, highlightItem } = useApp();

  if (lineItems.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: '24px' }}>
      <h2 className="section-title">
        <Route className="h-5 w-5 text-amber-500" />
        <span>Wire Schedule Run Details</span>
      </h2>

      <div className="wire-schedule-cards-container">
        {lineItems.map((item, rowIdx) => {
          const origin = item['Circuit_Origin'] || 'Unknown Origin';
          const dest = item['Circuit_Destination'] || 'Unknown Destination';
          const details = calculateSingleItemTotals(item);
          const isActive = activeRowIndex === rowIdx;

          return (
            <details
              key={rowIdx}
              className={`wire-schedule-card ${isActive ? 'active-card' : ''}`}
              onClick={(e) => {
                if ((e.target as HTMLElement).tagName !== 'INPUT') {
                  highlightItem(item['_bbox'], rowIdx);
                }
              }}
            >
              <summary>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Route className="h-4 w-4 text-amber-500" />
                  <span>{origin}</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                  <span>{dest}</span>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className="badge badge-wire">
                    {details.wireQuantity}x {details.tradeSize || 'N/A'}
                  </span>
                  <span className="badge badge-conduit">
                    {details.conduitSize || 'N/A'}" {details.conduitType || ''} (
                    {details.conduitLength.toFixed(0)} ft)
                  </span>
                </div>
              </summary>

              <div className="card-details-body">
                <div className="details-grid">
                  {/* Conductors & Wires Section */}
                  <div className="details-section">
                    <h4
                      style={{
                        margin: '0 0 10px 0',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'var(--text-main)',
                      }}
                    >
                      <Zap className="h-4 w-4 text-amber-500" />
                      <span>Conductors &amp; Wires</span>
                    </h4>

                    <div className="details-row">
                      <span>Conductor Size:</span>
                      <strong>{details.tradeSize || 'N/A'}</strong>
                    </div>

                    <div className="details-row">
                      <span>Quantity (Phase/Hot):</span>
                      <strong>{details.wireQuantity}</strong>
                    </div>

                    <div className="details-row">
                      <span>Neutral:</span>
                      <strong>
                        {item['Neutral'] || 'None'}{' '}
                        {details.neutralAdder ? '(+1 conductor)' : ''}
                      </strong>
                    </div>

                    <div className="details-row">
                      <span>Parallel Runs:</span>
                      <strong>{details.parallel}</strong>
                    </div>

                    <div className="details-row">
                      <span>Inverter Multiplier:</span>
                      <strong>{details.inverterMultiplier}x</strong>
                    </div>

                    <div className="details-row highlight-total">
                      <span>Total Phase/Neutral Wire:</span>
                      <strong>{details.wireTotal.toFixed(1)} ft</strong>
                    </div>

                    {details.groundAdder === 1 && (
                      <>
                        <div
                          style={{
                            height: '1px',
                            background: 'var(--border-color)',
                            margin: '8px 0',
                          }}
                        />
                        <div className="details-row">
                          <span>Ground Size:</span>
                          <strong>{details.groundSize}</strong>
                        </div>
                        <div className="details-row highlight-total">
                          <span>Total Ground Wire:</span>
                          <strong>{details.groundTotal.toFixed(1)} ft</strong>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Conduit Specification Section */}
                  <div className="details-section">
                    <h4
                      style={{
                        margin: '0 0 10px 0',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'var(--text-main)',
                      }}
                    >
                      <Circle className="h-4 w-4 text-amber-500" />
                      <span>Conduit Specification</span>
                    </h4>

                    <div className="details-row">
                      <span>Conduit Size:</span>
                      <strong>{details.conduitSize || 'N/A'}</strong>
                    </div>

                    <div className="details-row">
                      <span>Conduit Type:</span>
                      <strong>{details.conduitType || 'N/A'}</strong>
                    </div>

                    <div className="details-row">
                      <span>One-way Length:</span>
                      <strong>{details.length} ft</strong>
                    </div>

                    <div className="details-row highlight-total">
                      <span>Total Conduit Distance:</span>
                      <strong>{details.conduitLength.toFixed(1)} ft</strong>
                    </div>
                  </div>
                </div>
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
};
